import { AxiosResponse } from 'axios';
import React, { useEffect, useMemo, useState } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';
import { useQueryClient } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';

import Collapsible from 'components/collapsible';
import LoadingSpinner from 'components/loading-spinner';
import TextField from 'components/text-field';

import { SnackbarActionTypes, useSnackbarDispatch } from 'contexts/snackbar/SnackbarContext';

import { PutSiloBufferRequest } from 'views/site-settings/features/inventory-silo-management/types/put-silo-buffer-request.type';

import ActionButtons from 'components/action-buttons';
import Switch from 'components/switch';
import { usePost } from 'hooks/use-post';
import { useSiteSettingsState } from 'views/site-settings/context/site-settings-context';
import { useUserFeatures } from 'views/site-settings/hooks/use-user-features';
import { putSiloBuffer } from '../api/silo-management';
import { Alert, Grid, useMediaQuery, useTheme } from '@mui/material';

type ParamTypes = {
  globalSiteId: string;
};

type Props = {};

const PAGE_NAME_ID = 'siteSettings.editSiteSettings.siloFillPercentage';

const SiloBuffer: React.FC<Props> = ({}) => {
  const intl = useIntl();
  const queryClient = useQueryClient();
  const params = useParams<ParamTypes>();
  const snackbarDispatch = useSnackbarDispatch();
  const { canEditSiteSettings } = useUserFeatures();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const {
    siteSettingsQuery: { data: settings, isLoading },
  } = useSiteSettingsState();

  if (isLoading) return <LoadingSpinner />; // bad solution?
  if (!settings) return <></>;

  // #region STATE
  const [edit, setEdit] = useState(false);
  const [isBufferChanged, setIsBufferChanged] = useState<boolean>(false);
  // #endregion STATE

  // #region FORMS
  const { handleSubmit, control, reset, getValues } = useForm<PutSiloBufferRequest>({
    defaultValues: useMemo(() => {
      return {
        capacityBufferPercentage: settings.capacityBufferPercentage * 100,
        useStaticFill: settings.useStaticFill,
        staticFillPercentage: settings.staticFillPercentage * 100,
        consumptionBufferDays: settings.consumptionBufferDays,
      };
    }, [settings]),
    shouldUnregister: true,
  });

  const watchSiloFillToMaximum = useWatch({
    control,
    name: 'useStaticFill',
  });
  // #endregion FORMS

  // #region POST DATA
  const updateSiloBuffer = usePost<AxiosResponse, PutSiloBufferRequest>(
    (request) => putSiloBuffer(params.globalSiteId, request),
    () => {
      queryClient.invalidateQueries({
        queryKey: ['getSiteSettings', params.globalSiteId],
        exact: true
      });
      snackbarDispatch({
        type: SnackbarActionTypes.SUCCESS_SNACKBAR,
        payload: { msg: intl.formatMessage({ id: 'snackbar.success.default' }) },
      });
      setEdit(false);
      setIsBufferChanged(false);
    },
    (error) => {
      snackbarDispatch({
        type: SnackbarActionTypes.ERROR_SNACKBAR,
        payload: { msg: intl.formatMessage({ id: 'snackbar.errors.default' }), error: error },
      });
      setEdit(false);
      setIsBufferChanged(false);
    }
  );
  // #endregion POST DATA

  // #region HANDLERS
  const handleUpdateSiloBuffer = (request: PutSiloBufferRequest) => {
    request.staticFillPercentage = request.staticFillPercentage / 100;
    request.capacityBufferPercentage = request.capacityBufferPercentage / 100;

    updateSiloBuffer.mutate(request);
  };

  const handleEdit = (edited: boolean) => setEdit(edited);

  const handleCancel = () => {
    reset();
    setEdit(false);
    setIsBufferChanged(false);
  };

  // #endregion HANDLERS

  // #region HOOKS
  useEffect(() => {
    reset({
      useStaticFill: settings.useStaticFill,
      staticFillPercentage: settings.staticFillPercentage * 100,
      capacityBufferPercentage: settings.capacityBufferPercentage * 100,
    });
  }, [reset, settings]);
  // #endregion HOOKS

  return (
    <Collapsible label={intl.formatMessage({ id: `${PAGE_NAME_ID}.title` })} open>
      <>
        {isBufferChanged && (
          <Alert severity="warning" sx={{ mb: 4 }}>
            <FormattedMessage id={`${PAGE_NAME_ID}.dialog.warning`} />
          </Alert>
        )}
        <form onSubmit={handleSubmit(handleUpdateSiloBuffer)}>
          <Grid container spacing={6} pb={4} px={isMobile ? 4 : 0}>
            <Grid size={12}>
              <ActionButtons
                isDisabled={canEditSiteSettings}
                isEditing={edit}
                onEdit={handleEdit}
                onCancel={handleCancel}
                onSave={handleSubmit(handleUpdateSiloBuffer)}
              />
            </Grid>

            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                disableDecimal
                dark
                disabled={!edit || watchSiloFillToMaximum}
                fullWidth
                label={intl.formatMessage({ id: `${PAGE_NAME_ID}.dialog.capacityBufferPercentage` })}
                endAdornment={'%'}
                type="number"
                onChange={(event) => {
                  settings.capacityBufferPercentage !== Number(event.target.value)
                    ? setIsBufferChanged(true)
                    : setIsBufferChanged(false);
                }}
                controller={{
                  name: 'capacityBufferPercentage',
                  control: control,
                  rules: {
                    required: intl.formatMessage({ id: 'form.rules.required' }),
                    validate: {
                      positive: (v) => (parseInt(v) >= 0 ? true : intl.formatMessage({ id: 'form.rules.moreThanZero' })),
                    },
                  },
                }}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                dark
                disabled={!edit || watchSiloFillToMaximum}
                fullWidth
                label={intl.formatMessage({ id: `${PAGE_NAME_ID}.dialog.dynamicConsumptionBuffer` })}
                endAdornment={'days'}
                type="number"
                onChange={(event) => {
                  settings.consumptionBufferDays !== Number(event.target.value) ? setIsBufferChanged(true) : setIsBufferChanged(false);
                }}
                controller={{
                  name: 'consumptionBufferDays',
                  control: control,
                  rules: {
                    required: intl.formatMessage({ id: 'form.rules.required' }),
                    validate: {
                      positive: (v) => (parseInt(v) >= 0 ? true : intl.formatMessage({ id: 'form.rules.moreThanZero' })),
                    },
                  },
                }}
              />
            </Grid>
            {updateSiloBuffer.isPending && <LoadingSpinner overlay />}

            <Grid size={{ xs: 12, md: 6 }}>
              <Switch
                disabled={!edit}
                dark
                label={intl.formatMessage({ id: `${PAGE_NAME_ID}.dialog.setInventoryBufferLabel` })}
                controller={{
                  name: 'useStaticFill',
                  control: control,
                }}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                dark
                fullWidth
                disableDecimal
                type="number"
                disabled={!(edit && watchSiloFillToMaximum)}
                label={intl.formatMessage({ id: `${PAGE_NAME_ID}.dialog.refillSiloToLabel` })}
                endAdornment={'%'}
                onChange={(event) => {
                  settings.staticFillPercentage !== Number(event.target.value) ? setIsBufferChanged(true) : setIsBufferChanged(false);
                }}
                controller={{
                  name: 'staticFillPercentage',
                  control: control,
                  rules: {
                    required: intl.formatMessage({ id: 'form.rules.required' }),
                    validate: {
                      positive: (v) =>
                        parseInt(v) >= 0 && parseInt(v) <= 90
                          ? true
                          : intl.formatMessage({ id: 'form.rules.between' }, { lower: '0', upper: '90' }),
                    },
                  },
                }}
              />
            </Grid>
          </Grid>
        </form>
      </>
    </Collapsible>
  );
};

export default React.memo(SiloBuffer);
